import Skeleton from "@/components/Skeleton/Skeleton";
import { VisualizationProps } from "@/components/visualizations/Visualization";
import { formatPercentage } from "@/pages/dashboard/utils/formatters";
import {
  ArcElement,
  ChartData,
  Chart as ChartJS,
  CoreChartOptions,
  Legend,
  PluginChartOptions,
  Tooltip,
} from "chart.js";
import { _DeepPartialObject } from "chart.js/dist/types/utils";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Doughnut } from "react-chartjs-2";
import NoDataPoints from "../NoDataPoints/NoDataPoints";
import useVisualization from "../useVisualization";
import styles from "./DonutChart.module.scss";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

export type DonutChartData = { label: string; value: number }[];

export interface DonutChartProps extends VisualizationProps {
  data: DonutChartData;
}

export default function DonutChart(props: DonutChartProps) {
  const { data, loading } = props;
  const { formatLabel, colorPalette } = useVisualization(props);

  const chartData: ChartData<"doughnut", number[], string> = {
    labels: data.map((row) => row.label),
    datasets: [
      {
        label: "Environmental footprint",
        data: data.map((row) => row.value),
        backgroundColor: colorPalette,
      },
    ],
  };

  const total = data.map((row) => row.value).reduce((prev, curr) => curr + prev, 0);

  const options: _DeepPartialObject<CoreChartOptions<"doughnut"> & PluginChartOptions<"doughnut">> =
    {
      layout: {
        padding: 20,
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context: any) => formatLabel(context.raw),
          },
        },
        legend: { display: false },
        datalabels: {
          display: "auto",
          textStrokeColor: "#fff",
          textStrokeWidth: 3,
          font: {
            weight: 600,
          },
          color: "#000",
          formatter: (value: any) => {
            return formatPercentage(value / total);
          },
        },
      },
    };

  const sum = data.reduce((a, b) => a + b.value, 0);

  return (
    <div className={styles.DonutChart}>
      {!loading && (data.length === 0 || sum === 0) ? (
        <NoDataPoints />
      ) : (
        <>
          <div className={styles.chart}>
            {loading ? (
              <Skeleton
                style={{
                  aspectRatio: 1,
                  maxWidth: "100%",
                  borderRadius: "100%",
                  height: "auto",
                }}
              />
            ) : (
              <Doughnut
                data={chartData}
                options={options}
              />
            )}
          </div>
          <div className={styles.legend}>
            {loading
              ? [...Array(5)].map((_, i) => (
                  <div key={i}>
                    <Skeleton style={{ width: "60%", height: 12, marginBottom: 4 }} />
                    <Skeleton style={{ width: "20%", height: 12 }} />
                  </div>
                ))
              : data.map((row, index) => {
                  return (
                    <div
                      key={"legend" + index}
                      className={styles["legend-row"]}
                    >
                      <div className={styles["legend-header"]}>
                        <span
                          className={styles["legend-color"]}
                          style={{ background: colorPalette[index % colorPalette.length] }}
                        ></span>
                        <span className={styles["legend-label"]}>{row.label}</span>
                      </div>
                      <div className={styles["legend-values"]}>
                        {formatPercentage(row.value / total)} | {formatLabel(row.value)}
                      </div>
                    </div>
                  );
                })}
          </div>
        </>
      )}
    </div>
  );
}
